import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 100;

const L2_ACTIVATE_DELAY = 100;
const L2_DEACTIVATE_DELAY = 100;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeL2Item - The currently active L2 menu item.
 * @property {HTMLElement | null} activeL3Panel - The currently active L3 panel.
 * @property {string | null} lastPointerType - The last pointer type used (mouse, touch, pen).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 * @property {HTMLElement[]} [l3panel] - The L3 panels in the drawer.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Global pointermove listener to detect when cursor leaves the menu area
    // This is a fallback for drawer-style menus where pointerleave might not fire reliably
    document.addEventListener(
      'pointermove',
      this.#handleGlobalPointerMove,
      { signal: this.#abortController.signal }
    );

    // Global click listener to close menu when tapping outside on touch devices
    document.addEventListener(
      'click',
      this.#handleGlobalClick,
      { signal: this.#abortController.signal }
    );

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeL2Item: null,
    activeL3Panel: null,
    /** @type {string | null} */
    lastPointerType: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /** Get the overflow menu. */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /** Whether the overflow menu is hovered. @returns {boolean} */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Track the pointer type for use in click handlers
    if ('pointerType' in event) {
      this.#state.lastPointerType = /** @type {PointerEvent} */ (event).pointerType;
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    // Close L3 panel when switching to a different L1 menu
    if (this.#state.activeL2Item) {
      this.#state.activeL2Item.removeAttribute('data-l2-active');
      this.#state.activeL2Item = null;
    }
    if (this.#state.activeL3Panel) {
      this.#state.activeL3Panel.removeAttribute('data-l3-active');
      this.#state.activeL3Panel = null;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    // On touch devices, don't auto-close on pointerleave - user must tap elsewhere or navigate
    if (this.#isTouchDevice()) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    // Also close L3 panel when menu is closed
    if (this.#state.activeL2Item) {
      this.#state.activeL2Item.removeAttribute('data-l2-active');
      this.#state.activeL2Item = null;
    }
    if (this.#state.activeL3Panel) {
      this.#state.activeL3Panel.removeAttribute('data-l3-active');
      this.#state.activeL3Panel = null;
    }

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Activate L2 menu item to show L3 panel
   * @param {PointerEvent | FocusEvent} event
   */
  activateL2 = (event) => {
    // Track the pointer type for use in click handlers
    if ('pointerType' in event) {
      this.#state.lastPointerType = /** @type {PointerEvent} */ (event).pointerType;
    }

    this.#debouncedDeactivateL2.cancel();
    this.#debouncedActivateL2Handler(event);
  };

  /**
   * Activate L2 handler with delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateL2Handler = (event) => {
    this.#debouncedDeactivateL2.cancel();

    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('.mega-menu-drawer__link[data-l2-index]');
    if (!link) return;

    const l2Index = link.getAttribute('data-l2-index');
    const item = link.closest('.mega-menu-drawer__item[data-has-children]');

    if (!item || item === this.#state.activeL2Item) return;

    // Deactivate previous L2 item and its L3 panel
    if (this.#state.activeL2Item) {
      this.#state.activeL2Item.removeAttribute('data-l2-active');
    }
    if (this.#state.activeL3Panel) {
      this.#state.activeL3Panel.removeAttribute('data-l3-active');
    }

    // Activate new L2 item
    this.#state.activeL2Item = /** @type {HTMLElement} */ (item);
    item.setAttribute('data-l2-active', '');

    // Find and activate matching L3 panel
    const drawer = item.closest('.mega-menu-drawer');
    const l3Panel = drawer?.querySelector(`.mega-menu-drawer__panel--l3[data-l3-for="${l2Index}"]`);
    if (l3Panel) {
      this.#state.activeL3Panel = /** @type {HTMLElement} */ (l3Panel);
      l3Panel.setAttribute('data-l3-active', '');
    }
  };

  #debouncedActivateL2Handler = debounce(this.#activateL2Handler, L2_ACTIVATE_DELAY);

  /**
   * Deactivate L2 menu item
   * @param {PointerEvent | FocusEvent} event
   */
  deactivateL2 = (event) => {
    this.#debouncedActivateL2Handler.cancel();

    // On touch devices, don't auto-close on pointerleave - user must tap elsewhere or navigate
    if (this.#isTouchDevice()) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('.mega-menu-drawer__item[data-has-children]');

    // Only deactivate if this is the active L2 item
    if (item === this.#state.activeL2Item) {
      this.#debouncedDeactivateL2();
    }
  };

  /** Deactivate L2 item immediately. */
  #deactivateL2 = () => {
    if (this.#state.activeL2Item) {
      this.#state.activeL2Item.removeAttribute('data-l2-active');
      this.#state.activeL2Item = null;
    }
    if (this.#state.activeL3Panel) {
      this.#state.activeL3Panel.removeAttribute('data-l3-active');
      this.#state.activeL3Panel = null;
    }
  };

  #debouncedDeactivateL2 = debounce(this.#deactivateL2, L2_DEACTIVATE_DELAY);

  /**
   * Keep drawer open when hovering over it
   * Cancels any pending L1 deactivation
   */
  keepDrawerOpen = () => {
    this.#debouncedDeactivate.cancel();
  };

  /**
   * Close drawer when leaving it
   * @param {PointerEvent} event
   */
  closeDrawer = (event) => {
    // On touch devices, don't auto-close on pointerleave - user must tap elsewhere or navigate
    if (this.#isTouchDevice()) {
      return;
    }

    const relatedTarget = event.relatedTarget;

    // Check if we're moving to another part of the menu
    if (relatedTarget instanceof Element) {
      const isInMenu = this.contains(relatedTarget) ||
        relatedTarget.closest('.menu-list__submenu') ||
        relatedTarget.closest('.mega-menu-drawer__panel--l3');

      if (isInMenu) {
        return; // Don't close if still in menu area
      }
    }

    // Leaving the menu entirely - close everything
    this.#debouncedDeactivateL2();
    this.#debouncedDeactivate();
  };

  /**
   * Keep L3 panel open when hovering over it
   * Cancels any pending deactivation for both L2 and L1
   */
  keepL3Open = () => {
    this.#debouncedDeactivateL2.cancel();
    this.#debouncedDeactivate.cancel();
  };

  /**
   * Close L3 panel when leaving it
   * Closes the entire menu if not moving to L2 or other menu items
   * @param {PointerEvent} event
   */
  closeL3Panel = (event) => {
    // On touch devices, don't auto-close on pointerleave - user must tap elsewhere or navigate
    if (this.#isTouchDevice()) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const l3Panel = event.target.closest('.mega-menu-drawer__panel--l3');
    if (!l3Panel) return;

    const relatedTarget = event.relatedTarget;

    // Check if we're moving to an element inside the menu system
    if (relatedTarget instanceof Element) {
      const l3Index = l3Panel.getAttribute('data-l3-for');
      const l2Link = this.querySelector(`.mega-menu-drawer__link[data-l2-index="${l3Index}"]`);

      // Moving back to the L2 link - keep everything open
      if (l2Link?.contains(relatedTarget)) {
        return;
      }

      // Moving to another part of the menu (L2 panel, another L3, etc.) - keep L1 open
      const isInMenu = this.contains(relatedTarget) ||
        relatedTarget.closest('.mega-menu-drawer') ||
        relatedTarget.closest('.menu-list__submenu');

      if (isInMenu) {
        this.#debouncedDeactivateL2();
        return;
      }
    }

    // Leaving the menu entirely - close everything
    this.#debouncedDeactivateL2();
    this.#debouncedDeactivate();
  };

  /**
   * Handle click on button-only links (prevent navigation, always open L3 panel)
   * Button-only links should never navigate - always show submenu instead
   * @param {MouseEvent} event
   */
  handleButtonOnlyClick = (event) => {
    event.preventDefault();
    // On touch devices, we need to manually trigger L2 activation
    // since pointerenter may not work reliably
    if (this.#isTouchDevice()) {
      this.#activateL2Handler(event);
    }
    // On mouse devices, L2 activation already happens via pointerenter
  };

  /**
   * Check if the current device is a touch-primary device (no hover support)
   * @returns {boolean}
   */
  #isTouchDevice = () => {
    // Check if device doesn't support hover (touch devices)
    return window.matchMedia('(hover: none)').matches;
  };

  /**
   * Handle click on L1 links with submenus on touch devices
   * First tap: prevent navigation, open submenu
   * Second tap (submenu already open): allow navigation
   * @param {PointerEvent | MouseEvent} event
   */
  handleL1TouchClick = (event) => {
    // Use tracked pointer type, or fall back to touch device detection
    const lastPointerType = this.#state.lastPointerType;
    const isTouch = lastPointerType === 'touch' || lastPointerType === 'pen' ||
      (lastPointerType === null && this.#isTouchDevice());

    if (!isTouch) {
      return; // Allow normal click behavior for mouse
    }

    if (!(event.target instanceof Element)) return;

    const menuItem = findMenuItem(event.target);
    if (!menuItem) return;

    // Check if this L1's submenu is already open
    const isAlreadyActive = menuItem === this.#state.activeItem && menuItem.ariaExpanded === 'true';

    if (isAlreadyActive) {
      // Submenu is already open - allow navigation (don't prevent default)
      return;
    }

    // First tap - prevent navigation and open submenu
    event.preventDefault();
    this.activate(event);
  };

  /**
   * Handle click on L2 links with L3 children on touch devices
   * First tap: prevent navigation, open L3 panel
   * Second tap (L3 panel already open): allow navigation
   * @param {PointerEvent | MouseEvent} event
   */
  handleL2TouchClick = (event) => {
    // On touch devices (no hover support), intercept click to show L3 first
    if (!this.#isTouchDevice()) {
      return; // Allow normal click behavior for mouse/hover devices
    }

    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('.mega-menu-drawer__link[data-l2-index]');
    if (!link) return;

    const item = link.closest('.mega-menu-drawer__item[data-has-children]');
    if (!item) return;

    // Check if this L2's L3 panel is already open
    const isAlreadyActive = item === this.#state.activeL2Item;

    if (isAlreadyActive) {
      // L3 panel is already open - allow navigation (don't prevent default)
      return;
    }

    // First tap - prevent navigation and open L3 panel
    event.preventDefault();

    // Manually trigger L2 activation since pointerenter may not fire on touch
    this.#activateL2Handler(event);
  };

  /**
   * Handle global click to close menu when tapping outside on touch devices
   * @param {MouseEvent} event
   */
  #handleGlobalClick = (event) => {
    // Only handle on touch devices
    if (!this.#isTouchDevice()) return;

    // Only check if there's an active menu
    if (!this.#state.activeItem) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    // Check if click is inside the menu area
    const isInMenuArea =
      this.contains(target) ||
      target.closest('.mega-menu-drawer__panel') ||
      target.closest('.menu-list__submenu');

    if (!isInMenuArea) {
      // Clicked outside menu area - close everything
      this.#deactivateL2();
      this.#deactivate();
    }
  };

  /**
   * Handle global pointer movement to close drawer when cursor leaves menu area
   * This is a fallback for drawer-style menus where pointerleave might not fire reliably
   * @param {PointerEvent} event
   */
  #handleGlobalPointerMove = (event) => {
    // Skip on touch devices - we use click handler instead
    if (this.#isTouchDevice()) return;

    // Only check if there's an active menu
    if (!this.#state.activeItem) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    // Check if cursor is inside the menu area (nav links, drawer panels, or drawer overlays)
    const isInMenuArea =
      this.contains(target) ||
      target.closest('.mega-menu-drawer__panel') ||
      target.closest('.menu-list__submenu');

    if (isInMenuArea) {
      // Cancel any pending deactivation if cursor is back in menu area
      this.#debouncedDeactivate.cancel();
    } else {
      // If cursor is outside menu area, schedule deactivation
      this.#debouncedDeactivate();
    }
  };

  /** Preload images that are set to load lazily. */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
